import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useMDCockpit } from '../../../../hooks/useMDCockpit';
import { useDraggable } from '../../../../hooks/useDraggable';
import MissionService from '../../../../../../../service/MissionService';
import styles from './AIPanel.module.scss';

/* 4-point diamond sparkle — same icon as CommandBar */
const SparkIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <path d="M0.950012 10.123C0.691982 10.016 0.691983 9.64988 0.950012 9.54288L2.74884 8.79678C5.48631 7.66161 7.66151 5.4864 8.79669 2.74893L9.54279 0.949127C9.64987 0.69142 10.0158 0.691451 10.1229 0.949127L10.869 2.74893C12.0041 5.4864 14.1793 7.6616 16.9168 8.79678L18.7166 9.54288C18.9743 9.64999 18.9743 10.0159 18.7166 10.123L16.9168 10.869C14.1793 12.0042 12.0041 14.1794 10.869 16.9169L10.1229 18.7157C10.0159 18.9738 9.64979 18.9738 9.54279 18.7157L8.79669 16.9169C7.66151 14.1794 5.48631 12.0042 2.74884 10.869L0.950012 10.123Z" stroke="#CCF54E" stroke-width="1.51204"/>
</svg>
);

const MaximizeIcon = () => (
  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
    <rect x="1.5" y="1.5" width="9" height="9" stroke="currentColor" strokeWidth="1.2" />
  </svg>
);

const MinimizeIcon = () => (
  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
    <rect x="1" y="3" width="7" height="7" stroke="currentColor" strokeWidth="1.2" />
    <path d="M4 1h7v7H9V3H4V1z" fill="currentColor" />
  </svg>
);

const CloseIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const CursorSelectIcon = () => (
 <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
  <path d="M9.33333 2.7334L8 4.00007" stroke="#888888" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M3.40013 5.33338L1.4668 4.80005" stroke="#888888" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M4.00007 8L2.7334 9.33333" stroke="#888888" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M4.7998 1.46655L5.33314 3.39989" stroke="#888888" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M6.02444 6.46002C5.99859 6.39912 5.99152 6.33189 6.00415 6.26695C6.01677 6.202 6.04851 6.14231 6.09529 6.09553C6.14207 6.04875 6.20176 6.01701 6.2667 6.00439C6.33164 5.99177 6.39888 5.99883 6.45978 6.02469L13.7931 9.02469C13.8584 9.05146 13.9135 9.09825 13.9505 9.15833C13.9874 9.2184 14.0044 9.28866 13.9989 9.35899C13.9934 9.42932 13.9658 9.4961 13.9199 9.54972C13.8741 9.60333 13.8124 9.64102 13.7438 9.65735L10.8444 10.3514C10.7248 10.3799 10.6153 10.4411 10.5282 10.5281C10.4412 10.615 10.3799 10.7244 10.3511 10.844L9.65778 13.744C9.64162 13.8129 9.60397 13.8748 9.55029 13.9209C9.4966 13.9669 9.42966 13.9947 9.35914 14.0002C9.28862 14.0057 9.21819 13.9886 9.15802 13.9514C9.09785 13.9142 9.05107 13.8589 9.02444 13.7934L6.02444 6.46002Z" stroke="#888888" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
);

const SendArrowIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
  <path d="M3.3335 6.66667L8.00016 2L12.6668 6.66667" stroke="#CCF54E" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M8 13V2" stroke="#CCF54E" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
);

const GeoFeatureChipIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M2.5 2.5h2.2v2.2H2.5zM9.3 2.5h2.2v2.2H9.3zM2.5 9.3h2.2v2.2H2.5zM9.3 9.3h2.2v2.2H9.3z" stroke="currentColor" strokeWidth="0.9" />
    <path d="M4.7 3.6h4.6M3.6 4.7v4.6M4.7 10.4h4.6M10.4 4.7v4.6" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" />
  </svg>
);

function nowTimestamp() {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getUTCHours())}:${p(d.getUTCMinutes())} UTC`;
}

interface ParsedAnswer {
  message: string;
  constellation?: Array<{
    name: string;
    orbit_parameters?: { altitude_km: number; inclination_deg: number };
    planes: number;
    total_satellites: number;
    rationale?: string;
  }>;
  group_id?: string;
  satellite_ids?: string[];
  next_steps?: { action: string; label: string; prompt: string }[];
}

interface ChatMessage {
  role: 'user' | 'ai';
  text: string;
  timestamp: string;
  parsedAnswer?: ParsedAnswer;
  toolsCalled?: string[];
  thoughtTimeSeconds?: number;
}

/* ── CommandBar pill metrics (px) ── */
const PILL_WIDTH = 467;   // 8 icons × 40 + 3 dividers + 10 gaps × 12 + padding 24
const PANEL_WIDTH = 380;
const PANEL_HEIGHT = 460;
const LOADING_STATUSES = [
  'Analyzing...',
  'Optimizing...',
  'Phasing...',
  'Evaluating...',
  'Simulating...',
  'Generating...',
  'Finalizing...',
];

const AIPanel: React.FC = () => {
  const { toggleAIPanel, fetchSatellites, fetchGeoFeatures, aiPanelOpen, selectedItemId, selectedItemType, selectedGeoFeatureIds, geoFeatures } = useMDCockpit();
  const { mission_id, scenario_id } = useParams<{ mission_id: string; scenario_id: string }>();

  const [isExpanded, setIsExpanded] = useState(false);
  const [panelSize, setPanelSize] = useState({ width: PANEL_WIDTH, height: PANEL_HEIGHT });
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    setPanelSize({
      width: isExpanded ? 580 : PANEL_WIDTH,
      height: isExpanded ? 600 : PANEL_HEIGHT,
    });
  }, [isExpanded]);

  const initialPos = useMemo(() => {
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
    const aiIconRight = Math.round(vw / 2 + PILL_WIDTH / 2);
    const defaultHeight = isExpanded ? 600 : PANEL_HEIGHT;
    return {
      top: vh - 174 - defaultHeight,  // 174 = CommandBar top (bottom:110 + height:64)
      left: aiIconRight + 8,          // 8px gap right of icon
    };
  }, [isExpanded]);

  const { style: dragStyle, headerProps, pos: dragPos, setPos } = useDraggable(initialPos, aiPanelOpen);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingStatusIndex, setLoadingStatusIndex] = useState(0);
  const [loadingSeconds, setLoadingSeconds] = useState(0);

  const feedEndRef = useRef<HTMLDivElement>(null);

  const selectedGeoFeatures = useMemo(() => {
    const selectedIds = selectedGeoFeatureIds.length > 0
      ? selectedGeoFeatureIds
      : selectedItemType === 'geofeature' && selectedItemId
        ? [selectedItemId]
        : [];

    if (selectedIds.length === 0) return [];

    const selectedIdSet = new Set(selectedIds);
    return geoFeatures.filter((feature) => selectedIdSet.has(feature.id));
  }, [geoFeatures, selectedGeoFeatureIds, selectedItemId, selectedItemType]);

  useEffect(() => {
    feedEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (!loading) {
      setLoadingStatusIndex(0);
      setLoadingSeconds(0);
      return undefined;
    }

    const timer = window.setInterval(() => {
      setLoadingStatusIndex((idx) => (idx + 1) % LOADING_STATUSES.length);
    }, 1800);

    const secondsTimer = window.setInterval(() => {
      setLoadingSeconds((s) => s + 1);
    }, 1000);

    return () => {
      window.clearInterval(timer);
      window.clearInterval(secondsTimer);
    };
  }, [loading]);

  const handleSend = async (promptText: string) => {
    const trimmed = promptText.trim();
    if (!trimmed || loading) return;

    const userMsg: ChatMessage = { role: 'user', text: trimmed, timestamp: nowTimestamp() };
    setMessages(prev => [...prev, userMsg]);
    setInputText('');
    setLoadingStatusIndex(0);
    setLoading(true);

    const conversationHistory = messages.map(m => ({
      role: m.role === 'user' ? 'user' : 'assistant',
      content: m.text,
    }));

    const payload = {
      question: trimmed,
      conversation_history: conversationHistory,
      aoi: selectedGeoFeatures.map((feature) => feature.id),
    };

    const startTime = Date.now();
    try {
      const response = await MissionService.chatConstellationDesign(mission_id, scenario_id, payload);
      const data = response?.data;
      let parsed: ParsedAnswer | undefined;
      let toolsCalled: string[] = [];

      if (data) {
        toolsCalled = data.tools_called || [];
        if (data.answer) {
          try { parsed = JSON.parse(data.answer); } catch { parsed = { message: data.answer }; }
        }
      }

      const durationSeconds = Math.max(1, Math.round((Date.now() - startTime) / 1000));
      const aiMsg: ChatMessage = {
        role: 'ai',
        text: parsed?.message || 'Design process finished.',
        timestamp: nowTimestamp(),
        parsedAnswer: parsed,
        toolsCalled,
        thoughtTimeSeconds: durationSeconds,
      };
      setMessages(prev => [...prev, aiMsg]);

      if (mission_id && scenario_id) {
        void fetchSatellites(mission_id, scenario_id);
        void fetchGeoFeatures(mission_id, scenario_id);
      }
    } catch (err) {
      console.error('[MD-AI] Constellation Design Chat API failed', err);
      const durationSeconds = Math.max(1, Math.round((Date.now() - startTime) / 1000));
      setMessages(prev => [...prev, {
        role: 'ai',
        text: 'Sorry, I encountered an error. Please try again.',
        timestamp: nowTimestamp(),
        thoughtTimeSeconds: durationSeconds,
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(inputText);
    }
  };

  const canSend = inputText.trim().length > 0 && !loading;

  const handleResizeStart = (e: React.PointerEvent, direction: 'e' | 's' | 'se' | 'nw') => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = panelSize.width;
    const startHeight = panelSize.height;
    const startLeft = dragPos.left;
    const startTop = dragPos.top;

    const onPointerMove = (moveEvent: PointerEvent) => {
      let nextWidth = startWidth;
      let nextHeight = startHeight;
      let nextLeft = startLeft;
      let nextTop = startTop;

      if (direction === 'e' || direction === 'se') {
        const dx = moveEvent.clientX - startX;
        nextWidth = Math.max(300, Math.min(1000, startWidth + dx));
      }
      if (direction === 's' || direction === 'se') {
        const dy = moveEvent.clientY - startY;
        nextHeight = Math.max(300, Math.min(900, startHeight + dy));
      }

      if (direction === 'nw') {
        const dx = moveEvent.clientX - startX;
        const dy = moveEvent.clientY - startY;

        nextWidth = Math.max(300, Math.min(1000, startWidth - dx));
        nextHeight = Math.max(300, Math.min(900, startHeight - dy));

        const actualDx = startWidth - nextWidth;
        const actualDy = startHeight - nextHeight;

        nextLeft = startLeft + actualDx;
        nextTop = startTop + actualDy;

        setPos({ left: nextLeft, top: nextTop });
      }

      setPanelSize({ width: nextWidth, height: nextHeight });
    };

    const onPointerUp = () => {
      setIsResizing(false);
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
    };

    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
  };

  return (
    <div
      className={styles.panel}
      style={{
        ...dragStyle,
        width: panelSize.width,
        height: panelSize.height,
        zIndex: 30,
        transition: isResizing ? 'none' : 'width 0.2s ease-in-out, height 0.2s ease-in-out',
      }}
    >
      {/* ── Header (drag handle) ── */}
      <div className={styles.panelHeader} {...headerProps}>
        <div className={styles.panelHeaderLeft}>
          <SparkIcon />
          <span className={styles.headerTitle}>ANTARIS INTELLIGENCE</span>
        </div>
        <div className={styles.panelHeaderRight}>
          <button
            className={styles.headerBtn}
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            title={isExpanded ? "Collapse" : "Expand"}
          >
            {isExpanded ? <MinimizeIcon /> : <MaximizeIcon />}
          </button>
          <button className={styles.closeBtn} type="button" onClick={toggleAIPanel}>
            <CloseIcon />
          </button>
        </div>
      </div>

      {/* ── Body (chat feed) ── */}
      <div className={styles.panelBody}>
        <div className={styles.feed}>
          {messages.map((msg, i) => (
            msg.role === 'user' ? (
              <div key={i} className={styles.userMsg}>
                <div className={styles.userBody}>
                  <p className={styles.userText}>{msg.text}</p>
                  <span className={styles.userTime}>{msg.timestamp}</span>
                </div>
              </div>
            ) : (
              <div key={i} className={styles.aiMsg}>
                <div className={styles.aiBadge}>AI</div>
                <div className={styles.aiBody}>
                  <p className={styles.aiText}>{msg.text}</p>

                  {msg.parsedAnswer?.constellation && msg.parsedAnswer.constellation.length > 0 && (
                    <>
                      {msg.parsedAnswer.constellation.map((constellation, conIdx) => (
                        <div key={conIdx}>
                          <div className={styles.specCard}>
                            <div className={styles.specHeader}>
                              <span className={styles.specTag}>CONSTELLATION DESIGN</span>
                              <span className={styles.specLabel}>{constellation.name}</span>
                            </div>
                            <div className={styles.specGrid}>
                              <div className={styles.kv}><span className={styles.kvKey}>ALT</span><span className={styles.kvVal}>{constellation.orbit_parameters?.altitude_km} km</span></div>
                              <div className={styles.kv}><span className={styles.kvKey}>INC</span><span className={styles.kvVal}>{constellation.orbit_parameters?.inclination_deg}°</span></div>
                              <div className={styles.kv}><span className={styles.kvKey}>PLANES</span><span className={styles.kvVal}>{constellation.planes}</span></div>
                              <div className={styles.kv}><span className={styles.kvKey}>SATS</span><span className={`${styles.kvVal} ${styles.accent}`}>{constellation.total_satellites}</span></div>
                            </div>
                          </div>
                          {constellation.rationale && (
                            <div className={styles.rationaleContainer}>
                              <span className={styles.rationaleTitle}>DESIGN RATIONALE</span>
                              <p className={styles.rationaleText}>{constellation.rationale}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </>
                  )}

                  {msg.parsedAnswer?.next_steps && msg.parsedAnswer.next_steps.length > 0 && (
                    <div className={styles.nextSteps}>
                      {msg.parsedAnswer.next_steps.map((step, idx) => (
                        <button key={idx} className={styles.nextStepChip} type="button" onClick={() => handleSend(step.prompt)}>
                          {step.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {msg.thoughtTimeSeconds !== undefined && (
                    <span className={styles.thoughtTime}>Thought for {msg.thoughtTimeSeconds}s</span>
                  )}
                </div>
              </div>
            )
          ))}

          {loading && (
            <div className={styles.aiMsg}>
              <div className={styles.aiBadge}>AI</div>
              <div className={styles.aiBody}>
                <div className={styles.thinkingBlock}>
                  <div className={styles.thinkingHeader}>
                    <div className={styles.thinkingSpinner} />
                    <span className={styles.thinkingTitle}>ANTARIS INTELLIGENCE RUNNING · {loadingSeconds}s</span>
                  </div>
                  <div className={styles.statusBox}>
                    <span className={styles.thinkingText}>{LOADING_STATUSES[loadingStatusIndex]}</span>
                    <div className={styles.progressBar}>
                      <div className={styles.progressFill} style={{ width: `${((loadingStatusIndex + 1) / LOADING_STATUSES.length) * 100}%` }} />
                    </div>
                  </div>
                </div>

                <div className={styles.skeletonCard}>
                  <div className={styles.skeletonHeader}>
                    <div className={styles.shimmerLine} style={{ width: '40%', height: '6px' }} />
                    <div className={styles.shimmerLine} style={{ width: '65%', height: '10px', marginTop: '6px' }} />
                  </div>
                  <div className={styles.skeletonGrid}>
                    <div className={styles.shimmerGridCell} />
                    <div className={styles.shimmerGridCell} />
                    <div className={styles.shimmerGridCell} />
                    <div className={styles.shimmerGridCell} />
                  </div>
                </div>
              </div>
            </div>
          )}
          <div ref={feedEndRef} />
        </div>
      </div>

      {/* ── Composer ── */}
      <div className={styles.composerWrapper}>
        {selectedGeoFeatures.length > 0 && (
          <div className={styles.contextChipRow}>
            {selectedGeoFeatures.map((feature) => (
              <span key={feature.id} className={styles.gfChip} title={feature.name}>
                <GeoFeatureChipIcon />
                <span className={styles.gfChipLabel}>{feature.name}</span>
              </span>
            ))}
          </div>
        )}
        <div className={styles.inputArea}>
          <textarea
            className={styles.composerTextarea}
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="describe what you want to achieve from this newly made mission"
            disabled={loading}
            rows={3}
          />
        </div>
        <div className={styles.composerFooter}>
          <div className={styles.composerFooterLeft}>
            <CursorSelectIcon />
            <span className={styles.selectText}>select objects to contextualise</span>
          </div>
          <div className={`${styles.sendBtnContainer} ${canSend || loading ? styles.sendBtnContainerActive : ''}`}>
            <button
              className={`${styles.sendBtn} ${canSend ? styles.sendBtnReady : ''} ${loading ? styles.sendBtnLoading : ''}`}
              type="button"
              onClick={() => handleSend(inputText)}
              disabled={!canSend}
              aria-busy={loading}
            >
              {loading ? (
                <>
                  <span className={styles.sendProgressDot} />
                  <span className={styles.sendText}>Sending...</span>
                </>
              ) : (
                <>
                  <SendArrowIcon />
                  <span className={styles.sendText}>Send</span>
                </>
              )}
            </button>
            <div className={`${styles.accentBorder} ${styles.accentTopLeft}`} />
            <div className={`${styles.accentBorder} ${styles.accentTopRight}`} />
            <div className={`${styles.accentBorder} ${styles.accentBottomLeft}`} />
            <div className={`${styles.accentBorder} ${styles.accentBottomRight}`} />
          </div>
        </div>
      </div>

      {/* Resize handles */}
      <div className={styles.resizeHandleNW} onPointerDown={(e) => handleResizeStart(e, 'nw')} />
      <div className={styles.resizeHandleE} onPointerDown={(e) => handleResizeStart(e, 'e')} />
      <div className={styles.resizeHandleS} onPointerDown={(e) => handleResizeStart(e, 's')} />
      <div className={styles.resizeHandleSE} onPointerDown={(e) => handleResizeStart(e, 'se')} />
    </div>
  );
};

export default AIPanel;
